/**
 * Thin fetch/API-client module. Centralizes:
 *  - the base URL (relative by default; optional VITE_API_BASE_URL for a
 *    separate origin deployment)
 *  - sending the Entra session cookie on every call
 *  - unwrapping the {succeeded, message, errors, data} envelope
 *  - leaving /health* responses flat (they are not enveloped)
 *  - surfacing 401 / 503 / 400 messages to the operator (frontend spec §8)
 */

import type { Envelope } from "../types/api";

export class ApiError extends Error {
  readonly status: number;
  readonly errors: string[];

  constructor(message: string, status: number, errors: string[] = []) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errors = errors;
  }
}

function apiBase(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  if (!configured) return "";
  return configured.endsWith("/") ? configured.slice(0, -1) : configured;
}

function buildUrl(path: string): string {
  return `${apiBase()}${path}`;
}

async function parseJsonSafe(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function extractMessage(body: unknown): string | null {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.trim()) return record.message;
    const detail = record.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map((d) => (typeof d === "object" && d && "msg" in d ? String((d as Record<string, unknown>).msg) : String(d)))
        .filter(Boolean);
      if (parts.length) return parts.join("; ");
    }
    if (Array.isArray(record.errors) && record.errors.length) {
      return record.errors.map(String).join("; ");
    }
  }
  return null;
}

function extractErrors(body: unknown): string[] {
  if (body && typeof body === "object") {
    const record = body as Record<string, unknown>;
    if (Array.isArray(record.errors)) return record.errors.map(String);
  }
  return [];
}

function messageForStatus(status: number, body: unknown): string {
  const backendMessage = extractMessage(body);
  if (backendMessage) return backendMessage;

  switch (status) {
    case 401:
      return "You are not signed in. Sign in with Microsoft to continue.";
    case 403:
      return "Your Microsoft account does not have admin access yet.";
    case 503:
      return "Admin access is not set up on this server yet.";
    case 400:
      return "The server rejected this request (validation error).";
    case 404:
      return "Not found.";
    default:
      return `Request failed (${status}).`;
  }
}

/**
 * Call an enveloped `/api/v1/*` endpoint and return the unwrapped `data`.
 * The Entra session cookie is sent with `credentials: "include"`.
 */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");

  let res: Response;
  try {
    res = await fetch(buildUrl(path), { ...init, headers, credentials: "include" });
  } catch {
    throw new ApiError("Network error. Could not reach the admin console backend.", 0, []);
  }

  const body = await parseJsonSafe(res);

  if (!res.ok) {
    throw new ApiError(messageForStatus(res.status, body), res.status, extractErrors(body));
  }

  if (body && typeof body === "object" && "succeeded" in (body as Record<string, unknown>)) {
    const envelope = body as Envelope<T>;
    if (!envelope.succeeded) {
      throw new ApiError(envelope.message ?? "Request failed.", res.status, envelope.errors ?? []);
    }
    return envelope.data;
  }

  // Endpoint didn't return an envelope (shouldn't happen for /api/v1/* per spec) —
  // return the raw body rather than guessing at a shape.
  return body as T;
}

export interface HealthResult<T> {
  ok: boolean;
  status: number;
  body: T | null;
}

/**
 * Call a flat, non-enveloped `/health*` endpoint. Unlike apiFetch, this
 * never throws on non-2xx — callers need the body *and* the status for
 * `/health/ready`'s degraded 503 response.
 */
export async function healthFetch<T>(path: string): Promise<HealthResult<T>> {
  try {
    const res = await fetch(buildUrl(path), { headers: { Accept: "application/json" }, credentials: "include" });
    const body = await parseJsonSafe(res);
    return { ok: res.ok, status: res.status, body: body as T | null };
  } catch {
    return { ok: false, status: 0, body: null };
  }
}

export function buildQuery(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params as Record<string, string | number | undefined | null>)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}
